import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '../useApiMutation';
import { reviewKeys } from '@/lib/queryKeys';
import { reviewPlatformsV2Endpoints } from '@/lib/api/endpoints';
import { apiPost, operationsClient } from '@/lib/api';
import type { GenerateReplyDraftDto, GenerateReplyDraftResult } from '@/types/review';
import { invalidateReviewViews } from './invalidateReviewViews';

interface ExternalDraftVariables {
  /** Raw `ExternalReview.id` (no `ext_` prefix). */
  externalReviewId: string;
  options?: GenerateReplyDraftDto;
}

/**
 * (Re)generate an AI-drafted reply for an `ExternalReview`. Persists the new
 * draft on the row and returns the rendered text so the composer can render
 * it without round-tripping through the unified list.
 */
export function useGenerateReplyDraft() {
  const queryClient = useQueryClient();

  return useApiMutation<GenerateReplyDraftResult, ExternalDraftVariables>({
    mutationFn: async ({ externalReviewId, options }) =>
      apiPost<GenerateReplyDraftResult, GenerateReplyDraftDto>(
        operationsClient,
        reviewPlatformsV2Endpoints.replies.draft(externalReviewId),
        options ?? {},
      ),
    onSuccess: (_, variables) => {
      invalidateReviewViews(queryClient);
      void queryClient.invalidateQueries({
        queryKey: reviewKeys.detail('external', variables.externalReviewId),
      });
      toast.success('Draft regenerated');
    },
  });
}

/**
 * Regenerate the AI draft for an internal `ReviewResponse` row. The backend
 * is currently a stub that returns the existing row + `pending: true`; we
 * surface the body verbatim so the UI can render the right empty state.
 */
